import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { refreshTokenRequest } from './auth';
import { TokenResponse } from './types';

// Token state
let accessToken: string | null = null;
let refreshToken: string | null = null;
let tokenExpiresAt: number | null = null; // timestamp in ms

// Refresh state
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

// Storage keys
const STORAGE_KEY_TOKEN = 'onlyseeker_admin_token';
const STORAGE_KEY_REFRESH = 'onlyseeker_admin_refresh_token';
const STORAGE_KEY_EXPIRY = 'onlyseeker_admin_token_expiry';

// Buffer time before expiry to trigger refresh (30 seconds)
const EXPIRY_BUFFER_MS = 30 * 1000;

/**
 * Set tokens after login or refresh
 */
export const setTokens = (tokens: TokenResponse) => {
  accessToken = tokens.accessToken;
  refreshToken = tokens.refreshToken;
  // Calculate expiry timestamp from expiresIn (seconds)
  tokenExpiresAt = Date.now() + tokens.expiresIn * 1000;

  // Persist to localStorage
  localStorage.setItem(STORAGE_KEY_TOKEN, tokens.accessToken);
  localStorage.setItem(STORAGE_KEY_REFRESH, tokens.refreshToken);
  localStorage.setItem(STORAGE_KEY_EXPIRY, String(tokenExpiresAt));
};

/**
 * Clear all tokens (logout)
 */
export const clearTokens = () => {
  accessToken = null;
  refreshToken = null;
  tokenExpiresAt = null;

  localStorage.removeItem(STORAGE_KEY_TOKEN);
  localStorage.removeItem(STORAGE_KEY_REFRESH);
  localStorage.removeItem(STORAGE_KEY_EXPIRY);
};

/**
 * Get current refresh token (for logout API call)
 */
export const getRefreshToken = (): string | null => {
  return refreshToken;
};

/**
 * Load tokens from localStorage on init
 */
export const loadTokensFromStorage = (): boolean => {
  const storedToken = localStorage.getItem(STORAGE_KEY_TOKEN);
  const storedRefresh = localStorage.getItem(STORAGE_KEY_REFRESH);
  const storedExpiry = localStorage.getItem(STORAGE_KEY_EXPIRY);

  if (storedToken && storedRefresh && storedExpiry) {
    accessToken = storedToken;
    refreshToken = storedRefresh;
    tokenExpiresAt = parseInt(storedExpiry, 10);
    return true;
  }
  return false;
};

/**
 * Check if access token is expired or about to expire
 */
const isTokenExpired = (): boolean => {
  if (!tokenExpiresAt) return true;
  return Date.now() >= tokenExpiresAt - EXPIRY_BUFFER_MS;
};

/**
 * Subscribe to token refresh completion
 */
const subscribeToRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

/**
 * Notify all subscribers that refresh is complete
 */
const onRefreshComplete = (newToken: string) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

/**
 * Perform token refresh
 */
const performRefresh = async (): Promise<string> => {
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await refreshTokenRequest(refreshToken);
    setTokens(response);
    return response.accessToken;
  } catch (error) {
    // Refresh failed, clear tokens and redirect to login
    clearTokens();
    throw error;
  }
};

/**
 * Handle token refresh with queue for concurrent requests
 */
const handleTokenRefresh = async (): Promise<string> => {
  if (isRefreshing) {
    // Wait for ongoing refresh to complete
    return new Promise((resolve) => {
      subscribeToRefresh(resolve);
    });
  }

  isRefreshing = true;

  try {
    const newToken = await performRefresh();
    onRefreshComplete(newToken);
    return newToken;
  } finally {
    isRefreshing = false;
  }
};

// Create axios instance
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8084'
});

// Request interceptor - check token expiry before each request
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // Skip auth for login/refresh/logout endpoints
  const url = config.url ?? '';
  if (url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout')) {
    return config;
  }

  // If we have a token and it's expired, refresh it first
  if (accessToken && refreshToken && isTokenExpired()) {
    try {
      await handleTokenRefresh();
    } catch {
      // Refresh failed, will be handled by response interceptor
    }
  }

  // Add current access token to request
  if (accessToken) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

// Response interceptor - handle 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 401 and we haven't retried yet and we have a refresh token
    if (error.response?.status === 401 && !originalRequest._retry && refreshToken) {
      originalRequest._retry = true;

      try {
        const newToken = await handleTokenRefresh();
        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch {
        // Refresh failed, redirect to login
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }

    // For other 401 errors (no refresh token, or retry failed)
    if (error.response?.status === 401) {
      clearTokens();
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Legacy export for backward compatibility (deprecated)
export const setAuthToken = (token: string | null) => {
  if (token) {
    accessToken = token;
    localStorage.setItem(STORAGE_KEY_TOKEN, token);
  } else {
    clearTokens();
  }
};
